import { useSectionAccess } from "../../auth/useSectionAccess";
import { useEffect, useMemo, useState } from "react";
import { formatDate } from "../../lib/format";
import type { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { offerStagesApi } from "../../api/offerStages";
import type { OfferStage } from "../../api/offerStages";
import { DEFAULT_STAGE_COLOR } from "../offersBoard";
import { stageColorPickerValue } from "../../components/stageColors";
import { useToast } from "../../components/Toast";
import { useConfirm } from "../../components/ConfirmDialog";
import { Select } from "../../components/Select";
import Ic from "../../components/icons.jsx";
import type { ApiError } from "../../api/client";

/**
 * Admin "Offer stages" settings page:
 * the pipeline's configurable OPEN phases plus the two locked WON/LOST
 * outcomes. Modeled on AdminMarketsPage.tsx's list+inline-edit idiom rather
 * than a modal editor — renames/color/reorder are frequent, low-risk edits
 * better done in place (closer to AdminCategoriesPage's moveUp/moveDown, but
 * without needing a create/edit modal for name+color).
 *
 * `GET /api/offer-stages` returns every stage including archived ones (Task
 * 2's documented decision — see api/offerStages.ts) — this page is the one
 * place that actually shows the archived tail, as a read-only audit list
 * (no "unarchive" endpoint exists).
 */
export default function AdminOfferStagesPage() {
  const { canWrite, canFull } = useSectionAccess("offer_stages");
  const { t } = useTranslation();
  const toast = useToast();
  const confirm = useConfirm();

  const [stages, setStages] = useState<OfferStage[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [addName, setAddName] = useState("");
  // null until the admin picks a colour; a stage without one renders in its default colour.
  const [addColor, setAddColor] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  const [targetPicker, setTargetPicker] = useState<OfferStage | null>(null);

  async function refresh() {
    setStages(await offerStagesApi.list());
  }

  useEffect(() => {
    void refresh().catch((e: ApiError) => { toast.error(e.message); setStages([]); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const active = useMemo(
    () => (stages ?? []).filter((s) => !s.archived).sort((a, b) => a.position - b.position),
    [stages],
  );
  const archived = useMemo(
    () => (stages ?? []).filter((s) => s.archived).sort((a, b) => a.position - b.position),
    [stages],
  );

  async function reorderTo(ids: string[]) {
    try {
      await offerStagesApi.reorder(ids);
      await refresh();
    } catch (err) {
      toast.error((err as ApiError).message);
    }
  }

  async function moveUp(stage: OfferStage) {
    const idx = active.findIndex((s) => s.id === stage.id);
    if (idx <= 0) return;
    const ids = active.map((s) => s.id);
    [ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]];
    setBusyId(stage.id);
    try { await reorderTo(ids); } finally { setBusyId(null); }
  }

  async function moveDown(stage: OfferStage) {
    const idx = active.findIndex((s) => s.id === stage.id);
    if (idx === -1 || idx >= active.length - 1) return;
    const ids = active.map((s) => s.id);
    [ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]];
    setBusyId(stage.id);
    try { await reorderTo(ids); } finally { setBusyId(null); }
  }

  async function saveRow(stage: OfferStage, name: string, color: string | null) {
    setBusyId(stage.id);
    try {
      await offerStagesApi.update(stage.id, { name, color });
      await refresh();
      toast.success(t("offers.admin.stages.saved", "Stage saved."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusyId(null);
    }
  }

  async function addStage() {
    const name = addName.trim();
    if (!name) return;
    setAddBusy(true);
    try {
      await offerStagesApi.create({ name, color: addColor });
      setAddName("");
      setAddColor(null);
      await refresh();
      toast.success(t("offers.admin.stages.added", "Stage added."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setAddBusy(false);
    }
  }

  async function requestArchive(stage: OfferStage) {
    if (!(await confirm({
      message: t("offers.admin.stages.confirmArchive", { name: stage.name, defaultValue: `Archive "${stage.name}"?` }),
      danger: true,
    }))) return;
    setBusyId(stage.id);
    try {
      await offerStagesApi.archive(stage.id);
      await refresh();
      toast.success(t("offers.admin.stages.archived", "Stage archived."));
    } catch (err) {
      const e = err as ApiError;
      if (e.code === "OFFER_STAGE_TARGET_REQUIRED") {
        setTargetPicker(stage);
      } else {
        toast.error(e.message);
      }
    } finally {
      setBusyId(null);
    }
  }

  async function archiveWithTarget(targetStageId: string) {
    if (!targetPicker) return;
    const stage = targetPicker;
    setBusyId(stage.id);
    try {
      await offerStagesApi.archive(stage.id, targetStageId);
      setTargetPicker(null);
      await refresh();
      toast.success(t("offers.admin.stages.archived", "Stage archived."));
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div className="page-head">
        <div>
          <h1 style={{ margin: 0, fontSize: 22, color: "var(--ink)" }}>
            {t("offers.admin.stages.title", "Offer stages")}
          </h1>
          <div className="sub">
            {t("offers.admin.stages.subtitle", "Configure the sales pipeline's phases — order, name and color.")}
          </div>
        </div>
      </div>

      {stages === null ? (
        <div style={{ color: "var(--ink-3)" }}>{t("common.loading", "Loading…")}</div>
      ) : (
        <>
          <ul style={list}>
            {active.map((stage, i) => (
              <StageRow
                key={stage.id}
                stage={stage}
                index={i}
                total={active.length}
                busy={busyId === stage.id || !canWrite}
                onMoveUp={() => moveUp(stage)}
                onMoveDown={() => moveDown(stage)}
                onSave={(name, color) => saveRow(stage, name, color)}
                onArchive={() => { if (canFull) requestArchive(stage); }}
                canArchive={canFull}
              />
            ))}
          </ul>

          {canWrite && <section style={card}>
            <h2 style={cardTitle}>{t("offers.admin.stages.add", "New stage")}</h2>
            <div style={{ display: "flex", gap: 10, alignItems: "center", marginTop: 8, flexWrap: "wrap" }}>
              <input
                type="color"
                value={stageColorPickerValue(addColor)}
                onChange={(e) => setAddColor(e.target.value)}
                aria-label={t("offers.admin.stages.color", "Color")}
                style={swatchInput}
              />
              <input
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder={t("offers.admin.stages.namePlaceholder", "Stage name…")}
                className="input"
                style={{ flex: "1 1 220px", minWidth: 160 }}
                onKeyDown={(e) => { if (e.key === "Enter") void addStage(); }}
              />
              <button type="button" className="btn primary" disabled={addBusy || !addName.trim()} onClick={() => void addStage()}>
                <Ic.plus /> {t("action.add", "Add")}
              </button>
            </div>
            <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--ink-3)" }}>
              {t("offers.admin.stages.addHint", "New stages are always added as open (in-progress) phases, at the end of the pipeline.")}
            </p>
          </section>}

          {archived.length > 0 && (
            <section style={card}>
              <h2 style={cardTitle}>{t("offers.admin.stages.archivedTitle", "Archived stages")}</h2>
              <ul style={{ ...list, marginTop: 8 }}>
                {archived.map((stage) => (
                  <li key={stage.id} style={{ ...row, opacity: 0.65 }}>
                    <span aria-hidden style={{ ...swatch, background: stage.color || DEFAULT_STAGE_COLOR }} />
                    <span style={{ flex: 1, color: "var(--ink-2)" }}>{stage.name}</span>
                    <span style={archivedBadge}>{t("offers.admin.stages.archivedOn", "Archived {{date}}", { date: formatDate(stage.updatedAt) })}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </>
      )}

      {targetPicker && canFull && (
        <TargetStagePicker
          stage={targetPicker}
          options={active.filter((s) => s.id !== targetPicker.id)}
          busy={busyId === targetPicker.id}
          onCancel={() => setTargetPicker(null)}
          onConfirm={archiveWithTarget}
        />
      )}
    </div>
  );
}

function StageRow({
  stage, index, total, busy, onMoveUp, onMoveDown, onSave, onArchive, canArchive,
}: {
  stage: OfferStage;
  index: number;
  total: number;
  busy: boolean;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onSave: (name: string, color: string | null) => void;
  onArchive: () => void;
  canArchive: boolean;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState(stage.name);
  const [color, setColor] = useState<string | null>(stage.color || null);
  const locked = stage.kind !== "OPEN";
  const dirty = name.trim() !== stage.name || color !== (stage.color || null);

  return (
    <li style={row}>
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <button
          type="button"
          className="btn ghost sm"
          disabled={busy || index === 0}
          onClick={onMoveUp}
          aria-label={t("offers.admin.stages.moveUp", "Move up")}
          style={arrowBtn}
        >
          <Ic.arrowUp />
        </button>
        <button
          type="button"
          className="btn ghost sm"
          disabled={busy || index === total - 1}
          onClick={onMoveDown}
          aria-label={t("offers.admin.stages.moveDown", "Move down")}
          style={arrowBtn}
        >
          <Ic.arrowDown />
        </button>
      </div>

      <input
        type="color"
        value={stageColorPickerValue(color)}
        onChange={(e) => setColor(e.target.value)}
        aria-label={t("offers.admin.stages.color", "Color")}
        disabled={busy}
        style={swatchInput}
      />

      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        disabled={busy}
        className="input"
        style={{ flex: "1 1 220px", minWidth: 140 }}
      />

      <span style={kindBadge(stage.kind)}>
        {stage.kind === "WON" && <Ic.check />}
        {stage.kind === "LOST" && <Ic.x />}
        {stage.kind === "OPEN"
          ? t("offers.admin.stages.kind.open", "Open")
          : stage.kind === "WON"
            ? t("offers.admin.stages.kind.won", "Won")
            : t("offers.admin.stages.kind.lost", "Lost")}
      </span>

      {dirty && (
        <button type="button" className="btn primary sm" disabled={busy || !name.trim()} onClick={() => onSave(name.trim(), color)}>
          <Ic.check /> {t("action.save", "Save")}
        </button>
      )}

      {locked ? (
        <span title={t("offers.admin.stages.lockedHint", "Won/lost stages can't be archived — they're needed for the funnel.")} style={lockBadge}>
          <Ic.help /> {t("offers.admin.stages.locked", "Locked")}
        </span>
      ) : (
        <button type="button" className="btn ghost sm" disabled={busy || !canArchive} onClick={onArchive} style={{ color: "var(--err)" }}>
          {t("action.archive", "Archive")}
        </button>
      )}
    </li>
  );
}

function TargetStagePicker({
  stage, options, busy, onCancel, onConfirm,
}: {
  stage: OfferStage;
  options: OfferStage[];
  busy: boolean;
  onCancel: () => void;
  onConfirm: (targetStageId: string) => void;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState(options[0]?.id ?? "");

  return (
    <div style={overlay} onClick={onCancel}>
      <div style={dialog} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ margin: 0, fontSize: 18, color: "var(--ink)" }}>
          {t("offers.admin.stages.targetTitle", "Move offers before archiving")}
        </h2>
        <p style={{ margin: "8px 0 0", fontSize: 13, color: "var(--ink-3)" }}>
          {t("offers.admin.stages.targetHint", "\"{{name}}\" still has offers on it. Pick a stage to move them to.", { name: stage.name })}
        </p>
        <div style={{ marginTop: 14 }}>
          <Select<string>
            value={target}
            onChange={setTarget}
            ariaLabel={t("offers.admin.stages.targetTitle", "Move offers before archiving")}
            options={options.map((s) => ({ value: s.id, label: s.name }))}
          />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
          <button type="button" className="btn" onClick={onCancel}>{t("action.cancel", "Cancel")}</button>
          <button
            type="button"
            className="btn primary"
            disabled={busy || !target}
            onClick={() => onConfirm(target)}
            style={{ background: "var(--err)", borderColor: "var(--err)" }}
          >
            {t("offers.admin.stages.targetConfirm", "Move offers & archive")}
          </button>
        </div>
      </div>
    </div>
  );
}

const list: CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 6 };
const row: CSSProperties = {
  display: "flex", alignItems: "center", gap: 10,
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)",
  padding: "10px 12px", boxShadow: "var(--shadow-1)",
};
const arrowBtn: CSSProperties = { padding: "1px 6px", lineHeight: 1 };
const swatchInput: CSSProperties = { width: 32, height: 32, padding: 0, border: "1px solid var(--border)", borderRadius: 6, cursor: "pointer", flex: "none" };
const swatch: CSSProperties = { width: 14, height: 14, borderRadius: "50%", flex: "none" };
const card: CSSProperties = {
  background: "var(--surface)", border: "1px solid var(--border)", borderRadius: "var(--r-md)",
  boxShadow: "var(--shadow-1)", padding: 18,
};
const cardTitle: CSSProperties = { margin: 0, fontSize: 14, fontWeight: 600, color: "var(--ink)" };
function kindBadge(kind: OfferStage["kind"]): CSSProperties {
  const bg = kind === "WON" ? "var(--ok-bg)" : kind === "LOST" ? "var(--err-bg)" : "var(--surface-muted)";
  const color = kind === "WON" ? "var(--ok)" : kind === "LOST" ? "var(--err)" : "var(--ink-3)";
  return {
    display: "inline-flex", alignItems: "center", gap: 4,
    fontSize: 11, fontWeight: 600, padding: "3px 9px", borderRadius: 999,
    background: bg, color, flex: "none", whiteSpace: "nowrap",
  };
}
const lockBadge: CSSProperties = {
  display: "inline-flex", alignItems: "center", gap: 4,
  fontSize: 11, color: "var(--ink-4)", flex: "none", whiteSpace: "nowrap",
};
const archivedBadge: CSSProperties = { fontSize: 11, color: "var(--ink-4)", flex: "none", whiteSpace: "nowrap" };
const overlay: CSSProperties = {
  position: "fixed", inset: 0, background: "var(--overlay)",
  display: "grid", placeItems: "center", zIndex: 100, padding: 24,
};
const dialog: CSSProperties = {
  width: "min(480px, 100%)", background: "var(--surface)", padding: 22, borderRadius: "var(--r-lg)",
  boxShadow: "var(--shadow-2)", border: "1px solid var(--border)",
};
